"use client";

/**
 * OfflineRouteWarmer — pre-cachea rutas críticas Agro tras login online.
 *
 * Por qué existe:
 *   El SW puede cachear HTML/chunks vía SWR, pero solo para URLs ya visitadas.
 *   Sin pre-warm, navegar offline a una ruta nueva (p.ej. `/productores/87`
 *   cuando antes solo se vio `/productores/35`) carga el chunk del route group
 *   por primera vez → falla → ChunkLoadError → React #418/#423 → pantalla blanca.
 *
 * Qué hace:
 *   1) `router.prefetch(ruta)` para cada ruta crítica → Next.js carga los
 *      chunks JS de ese route group; el SW los caché vía SWR `/_next/static/`.
 *   2) Posta `WARM_NAV_CACHE` al SW para que él haga `fetch(ruta)` con
 *      `Accept: text/html` y guarde la respuesta como navigation, así el
 *      fallback SPA del SW navigate handler tiene HTML para devolver.
 *
 * Cuándo:
 *   - Una sola vez tras login + online + SW activo.
 *   - Re-warm cada 24h (deploys nuevos invalidan chunks por filename hash,
 *     conviene re-cachearlos).
 */

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { mergeDomainCache } from "@/lib/offline/cache";
import {
  finishDomain,
  startDomain,
  updateDomain,
} from "@/lib/offline/sync-progress";

const CRITICAL_ROUTES = [
  "/",
  "/productores",
  "/productores/nuevo",
  "/siembras",
  "/siembras/nueva",
  "/recepcion",
  "/ordenes",
  "/logistica",
  "/fincas-lotes",
  "/programacion",
  "/programas",
  "/admin/zonas-cacique",
  "/operaciones/autorizaciones",
  // C4 (21 jul): la captura de corte en campo del Conuco — Daury/Alexis
  // capturan en parcelas SIN señal; la ruta debe abrir offline.
  "/conuco/campo",
];

const WARM_FLAG_KEY = "cacique_warmer_last_run";
const WARM_TTL_MS = 24 * 60 * 60 * 1000; // 24h

function isOnline(): boolean {
  if (typeof navigator === "undefined") return true;
  return navigator.onLine !== false;
}

function shouldWarm(): boolean {
  try {
    const raw = localStorage.getItem(WARM_FLAG_KEY);
    if (!raw) return true;
    const last = Number(raw);
    if (!Number.isFinite(last)) return true;
    return Date.now() - last > WARM_TTL_MS;
  } catch {
    return true;
  }
}

function markWarmed(): void {
  try {
    localStorage.setItem(WARM_FLAG_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export default function OfflineRouteWarmer() {
  const router = useRouter();
  const { status } = useSession();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    if (typeof window === "undefined") return;
    if (status !== "authenticated") return;
    if (!isOnline()) return;
    if (!shouldWarm()) return;
    ranRef.current = true;

    // Prefetch en JS (carga chunks → SWR los cachea).
    for (const route of CRITICAL_ROUTES) {
      try {
        router.prefetch(route);
      } catch {
        /* ignore */
      }
    }

    // Pide al SW que cachee también el HTML de cada ruta como navigation.
    const postWarm = (urls: string[]) => {
      if (!("serviceWorker" in navigator) || !navigator.serviceWorker.controller) return;
      try {
        navigator.serviceWorker.controller.postMessage({ type: "WARM_NAV_CACHE", urls });
      } catch {
        /* ignore */
      }
    };
    postWarm(CRITICAL_ROUTES);

    // CRÍTICO: cachear UNA HTML de detalle dinámico por dominio. Sirve como
    // "template" para que el SW pueda responder offline a cualquier id del
    // mismo route group via wildcard fallback. Tras hidratar, router cliente
    // corrige el id y useOfflineDetail lee desde IDB.
    //
    // Además, descargamos TODOS los productores paginados al cache IDB para
    // que el listado y el detalle funcionen offline sin necesidad de haber
    // visitado cada uno. Reporta progreso a `lib/offline/sync-progress` para
    // que el header muestre el avance.
    (async () => {
      const DOMAIN = "productores";
      const PAGE_SIZE = 100;
      try {
        // Primera página: obtenemos total y el id de un productor para el SW.
        const firstRes = await fetch(`/api/productores?page=1&limit=${PAGE_SIZE}`, {
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        if (!firstRes.ok) return;
        const firstJson = (await firstRes.json()) as {
          data?: Array<{ id: number }>;
          total?: number;
          totalPages?: number;
        };
        const firstPage = Array.isArray(firstJson.data) ? firstJson.data : [];
        const total = typeof firstJson.total === "number" ? firstJson.total : firstPage.length;
        const totalPages = typeof firstJson.totalPages === "number"
          ? firstJson.totalPages
          : Math.max(1, Math.ceil(total / PAGE_SIZE));

        // Cachear HTML del detalle de UN productor como template del SW.
        const firstId = firstPage[0]?.id;
        if (firstId != null) {
          const detailRoute = `/productores/${firstId}`;
          try { router.prefetch(detailRoute); } catch { /* ignore */ }
          postWarm([detailRoute]);
        }

        if (firstPage.length === 0) return;

        // Arrancar progreso global.
        await startDomain(DOMAIN, "Productores", total);
        try { await mergeDomainCache(DOMAIN, firstPage); } catch { /* ignore */ }
        let done = firstPage.length;
        await updateDomain(DOMAIN, { done });

        // Descargar el resto de páginas secuencialmente — secuencial para no
        // saturar el server ni la conexión móvil.
        for (let page = 2; page <= totalPages; page++) {
          // Si el usuario se desconecta a mitad de camino, abortamos limpio.
          if (typeof navigator !== "undefined" && navigator.onLine === false) {
            await updateDomain(DOMAIN, { status: "error", error: "Conexión perdida" });
            return;
          }
          try {
            const r = await fetch(`/api/productores?page=${page}&limit=${PAGE_SIZE}`, {
              credentials: "include",
              headers: { Accept: "application/json" },
            });
            if (!r.ok) {
              await updateDomain(DOMAIN, { status: "error", error: `HTTP ${r.status}` });
              return;
            }
            const j = (await r.json()) as { data?: Array<{ id: number }> };
            const pageData = Array.isArray(j.data) ? j.data : [];
            if (pageData.length === 0) break;
            try { await mergeDomainCache(DOMAIN, pageData); } catch { /* ignore */ }
            done += pageData.length;
            await updateDomain(DOMAIN, { done });
          } catch (err) {
            await updateDomain(DOMAIN, {
              status: "error",
              error: err instanceof Error ? err.message : "Error de red",
            });
            return;
          }
        }

        await finishDomain(DOMAIN, true);
      } catch {
        // offline desde el primer fetch o sin datos — no marcar error.
      }
    })();

    markWarmed();
  }, [status, router]);

  return null;
}
